import { useEffect, useState } from 'react';
import { IFetchCommentParams, IComment } from '../../models';
import { postService } from '../../services/post.service';
import _ from 'lodash';
import { jwtDecode } from 'jwt-decode';
import { LocalStorageService } from '../../configuration';
import {
  fetchFollowingEmptyPages,
  nextCommentCursor,
} from '../../utils/comment-paging';

export const useComment = ({
  pageNumber,
  visible,
  postId,
  pageSize,
}: {
  pageNumber: number;
  visible: boolean;
  postId: string;
  pageSize?: number;
}) => {
  const [comments, setComments] = useState<IComment[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [pagingState, setPagingState] = useState<string>('');
  const [pageSizeClient, setPageSizeClient] = useState<number>(
    pageSize || 1 * pageNumber
  );
  const [totalLoadedPages, setTotalLoadedPages] = useState<number>(1);

  // Get role from JWT in localStorage
  let roles: string[] | undefined;
  const token = LocalStorageService.getItem('accessToken');
  if (token) {
    try {
      const decoded = jwtDecode<{ Role?: string[] }>(token);
      roles = decoded.Role;
    } catch {
      roles = undefined;
    }
  }

  const getComment = async () => {
    try {
      setLoading(true);
      const fetcher =
        !roles || roles.length === 0
          ? postService.getPublicCommentByPostId
          : postService.getCommentByPostId;
      const {
        data,
        hasMore: hasMoreResponse,
        pagingState: pagingStateResponse,
      } = await fetchFollowingEmptyPages<IComment>(
        (cursor) =>
          fetcher({
            pageNumber,
            pageSize: pageSize || 1,
            pagingState: cursor,
            postId,
          }),
        pagingState
      );

      if (pageNumber === 1) {
        setComments(data);
      } else {
        setComments((prev) => _.uniqBy([...prev, ...data], 'id'));
      }

      setHasMore(hasMoreResponse && nextCommentCursor(pagingStateResponse) !== null);
      setPagingState(pagingStateResponse);
      setTotalLoadedPages(pageNumber);
    } catch (error) {
      console.log('error', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshCurrentPage = async () => {
    try {
      setLoading(true);
      const {
        data,
        hasMore: hasMoreResponse,
        pagingState: pagingStateResponse,
      } = await postService.getCommentByPostId({
        pageNumber: 1,
        pageSize: pageSizeClient * totalLoadedPages, // Use total loaded pages
        pagingState: '',
        postId,
      });

      setComments(data);
      setHasMore(hasMoreResponse && nextCommentCursor(pagingStateResponse) !== null);
      setPagingState(pagingStateResponse);
    } finally {
      setLoading(false);
    }
  };

  const getCommentWhenComment = async () => {
    try {
      setLoading(true);
      const {
        data,
        hasMore: hasMoreResponse,
        pagingState: pagingStateResponse,
      } = await postService.getCommentByPostId({
        pageNumber: 1,
        pageSize: pageSizeClient * totalLoadedPages, // Use total loaded pages
        pagingState: '',
        postId,
      });

      setComments(data);
      setHasMore(hasMoreResponse && nextCommentCursor(pagingStateResponse) !== null);
      setPagingState(pagingStateResponse);
    } finally {
      setLoading(false);
    }
  };

  const refreshCommentClient = async () => {
    try {
      const {
        data,
        hasMore,
        pagingState: pagingStateResponse,
      } = await postService.getCommentByPostId({
        pageNumber: 1,
        pageSize: pageSizeClient,
        pagingState: '',
        postId,
      });

      setComments(data);
      setHasMore(hasMore && nextCommentCursor(pagingStateResponse) !== null);
      setPagingState(pagingStateResponse);
    } finally {
      setLoading(false);
    }
  };

  const updateComment = (commentId: string, newContent: string) => {
    setComments((prevComments) =>
      prevComments.map((comment) =>
        comment.id === commentId
          ? {
              ...comment,
              content: newContent,
            }
          : comment
      )
    );
  };

  const addComment = (newComment: IComment) => {
    setComments((prevComments) =>
      _.uniqBy([newComment, ...prevComments], 'id')
    );
  };

  const deleteComment = async (commentId: string, commentType: number = 0) => {
    try {
      await postService.deleteComment(commentId, commentType);
      // After successful deletion, refresh the current page
      await refreshCurrentPage();
      return true;
    } catch (error) {
      console.error('Error deleting comment:', error);
      throw error;
    }
  };

  const updateCommentLike = (commentId: string, type: number) => {
    setComments((prevComments) =>
      prevComments.map((comment) =>
        comment.id === commentId
          ? {
              ...comment,
              currentUserHasLike: type === 1,
              currentUserHasUnlike: type === 0,
              upvotesCount:
                type === 1 ? comment.upvotesCount + 1 : comment.upvotesCount,
              downvotesCount:
                type === 0
                  ? comment.downvotesCount + 1
                  : comment.downvotesCount,
            }
          : comment
      )
    );
  };

  useEffect(() => {
    if (!visible) {
      setComments([]);
      setPagingState('');
      setHasMore(false);
      return;
    }

    getComment();
  }, [pageNumber, visible]);

  return {
    data: comments,
    loading,
    hasMore,
    getComment,
    getCommentWhenComment,
    refreshCommentClient,
    updateComment,
    addComment,
    refreshCurrentPage,
    deleteComment,
    updateCommentLike,
  };
};
